import { BigNumber } from '@ethersproject/bignumber';
import { GetQuoteParameters } from '@reservoir0x/relay-sdk';
import { StoreType } from '@/store/mainStore';

export enum DataClientContractSpec {
  ERC721 = 'erc721',
  ERC1155 = 'erc1155',
}

export type DataClientNFT = {
  contractAddress: string;
  tokenId: string;
  ownerAddress: string;
  name: string;
  image: string;
  spec: DataClientContractSpec;
  count: string;
};

// represents one token selection step from the token selection flow
export type TokenSelectionStep = {
  status: 'current' | 'complete' | 'next';
  selectedTokens: DataClientNFT[];
};

export enum TxStepStatus {
  Unstarted = 'unstarted', // open circle, step is not started yet
  Pending = 'pending', // circle with pinging dot, waiting for user action
  Confirming = 'confirming', // circle with pinging dot but different text, user has confirmed, waiting for blockchain confirmation
  Confirmed = 'confirmed', // check mark, tx has been confirmed on chain
  Failed = 'failed', // x mark
}

/**
 * Identifiers for different transaction step types
 * Used to distinguish between different kinds of steps in the transaction flow
 */
export enum TxStepId {
  Connect = 'Connect',
  CheckAllowlist = 'Check Allowlist',
  SelectDelegateWallet = 'Select Allowlist Wallet',
  ReviewERC20Balance = 'Review ERC20 Balance',
  ApproveERC20Spending = 'Approve ERC20 spending',
  Mint = 'Complete Transaction', // primary mint
  Buy = 'Buy', // secondary buy
}

interface SecondaryComponentProps {
  updateStepState: (updates: Partial<TransactionStepState>) => void; // or the correct type for your function
}

/**
 * Configuration for a transaction step
 * Defines the static properties and UI behavior of a step
 * @property id - Unique identifier for the step
 * @property name - Display on UI the name for the step
 * @property primaryButtonText - Text for the primary button on the step
 * @property SecondaryComponent - Optional component to render in the secondary column of the step
 * @property alwaysShow - Whether to always show the step, even if it's not the current step. This is useful for steps that are not part of the current flow, but are still relevant to the user such as connect wallet step
 * @property hideOnceCompleted - Whether to hide the step once the flow is completed.
 */
export interface StepConfig {
  id: TxStepId;
  name: string;
  primaryButtonText?: string;
  SecondaryComponent?: React.ComponentType<SecondaryComponentProps>;
  alwaysShow?: boolean;
  hideOnceCompleted?: boolean;
}

/**
 * Result of step validation before execution
 * Used to determine if a step should proceed, be skipped, or show warnings
 * @property skip - If true, the step will be skipped and the flow will continue to the next step
 * @property error - Error message to display if the step fails
 * @property warning - Warning message to display if the step has a warning
 */
export interface ValidationResult {
  skip?: boolean;
  error?: string;
  warning?: string;
}

/**
 * Dynamic state of a transaction step
 * Tracks the current status and any relevant data like errors or transaction hashes
 * @property status - Current status of the step
 * @property descriptionByStatus - Map of statuses to their corresponding descriptions
 * @property error - Error message to display if the step fails
 * @property warning - Warning message to display if the step has a warning
 * @property etherscanLink - Link to the transaction on Etherscan
 * @property txHash - Transaction hash if the step is confirmed
 */
export interface TransactionStepState {
  status: TxStepStatus;
  descriptionByStatus: Record<TxStepStatus, string>;
  error?: string;
  warning?: string;
  etherscanLink?: string;
  txHash?: string;
}

/**
 * Complete transaction step definition
 * Combines configuration, state, and behavior for a single step in the transaction flow
 *
 * @property config - Static configuration defining the step's identity and UI behavior
 * @property state - Dynamic state tracking the step's current status and data
 * @property validateBefore - Optional validation function run before step execution
 * @property execute - Main execution function for the step's business logic
 * @property onSuccess - Optional callback executed after successful step completion
 * @property onError - Optional error handler for step-specific error handling
 */
export interface TransactionStep {
  config: StepConfig;
  state: TransactionStepState;
  validateBefore?: (
    store: StoreType,
    updateStepState: (updates: Partial<TransactionStepState>) => void,
  ) => Promise<ValidationResult | undefined>;
  execute?: (
    store: StoreType,
    updateStepState: (updates: Partial<TransactionStepState>) => void,
  ) => Promise<void>;
  onSuccess?: () => Promise<void>;
}

/**
 * Transaction data for relay-based cross-chain transactions
 * Used when executing transactions through the Relay protocol
 */
export interface RelayTransaction {
  to: string;
  value: string;
  data: string;
}

/**
 * Represents funds required for a transaction
 * Used for ERC20 balance validation and funding requirements
 */
export type RequiredFund = {
  address: string;
  amount: BigNumber;
  decimals: number;
  networkId: number;
};

// Omit chainId so we can dynamically add it to the parameters
export type GetQuoteParametersWithoutChainId = Omit<GetQuoteParameters, 'chainId'>;
